import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import client from "@/lib/api";
import { fmtDate, timeAgo, isToday, isOverdue, dueLabel } from "@/lib/format";
import { MessageSquare, ArrowRight, X } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const lastUpdate = (j) => (j.customer_updates && j.customer_updates.length ? j.customer_updates[j.customer_updates.length - 1] : null);

const VIEWS = [
  { key: "all", label: "All Jobs", fn: () => true },
  { key: "overdue", label: "Overdue", fn: (j) => isOverdue(j.customer_update_due_date) },
  { key: "due-today", label: "Due Today", fn: (j) => isToday(j.customer_update_due_date) },
  { key: "sent", label: "Sent", fn: (j) => (j.customer_updates || []).length > 0 },
  { key: "not-sent", label: "Not Sent", fn: (j) => (j.customer_updates || []).length === 0 },
];

export default function CustomerUpdates() {
  const [jobs, setJobs] = useState([]);
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState({ customer: "all", owner: "all" });
  const navigate = useNavigate();

  const view = params.get("filter") || "all";
  const setView = (v) => {
    if (v === "all") params.delete("filter");
    else params.set("filter", v);
    setParams(params, { replace: true });
  };

  useEffect(() => {
    client.get("/shipments").then((r) => setJobs(r.data.filter((j) => j.current_stage !== "Closed")));
  }, []);

  const owners = useMemo(() => [...new Set(jobs.map((j) => j.assigned_owner))].sort(), [jobs]);
  const customers = useMemo(() => [...new Set(jobs.map((j) => j.customer_name))].sort(), [jobs]);
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const viewDef = VIEWS.find((v) => v.key === view) || VIEWS[0];
  const count = (v) => jobs.filter(v.fn).length;

  const filtered = jobs.filter((j) => {
    if (!viewDef.fn(j)) return false;
    if (filters.customer !== "all" && j.customer_name !== filters.customer) return false;
    if (filters.owner !== "all" && j.assigned_owner !== filters.owner) return false;
    return true;
  });

  const anyFilter = filters.customer !== "all" || filters.owner !== "all";

  return (
    <Layout>
      <div className="mb-5">
        <p className="op-label mb-1">Customer communication</p>
        <h1 className="font-heading text-2xl font-semibold text-ink flex items-center gap-2">
          Broker Check-Ins
        </h1>
        <p className="text-sm text-ink-2 mt-1">Who's been updated, who's overdue, and what to say next.</p>
      </div>

      {/* Views */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            data-testid={`cu-view-${v.key}`}
            onClick={() => setView(v.key)}
            className={`h-8 px-3 rounded-sm text-[12.5px] font-medium border transition-colors flex items-center gap-1.5 ${view === v.key ? "bg-brand text-white border-brand" : "bg-surface text-ink-2 border-line hover:border-steel"}`}
          >
            {v.label}
            <span className={`font-mono-data text-[10.5px] ${view === v.key ? "text-white/70" : "text-ink-3"}`}>{count(v)}</span>
          </button>
        ))}
      </div>

      <div className="bg-surface border border-line rounded-md p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="min-w-[160px]">
          <label className="op-label block mb-1">Customer</label>
          <Select value={filters.customer} onValueChange={(v) => set("customer", v)}>
            <SelectTrigger data-testid="cu-customer-filter" className="h-9 border-line rounded-sm bg-surface text-[13px]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{customers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="min-w-[160px]">
          <label className="op-label block mb-1">Owner</label>
          <Select value={filters.owner} onValueChange={(v) => set("owner", v)}>
            <SelectTrigger data-testid="cu-owner-filter" className="h-9 border-line rounded-sm bg-surface text-[13px]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{owners.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {anyFilter && (
          <button data-testid="cu-clear-filters" onClick={() => setFilters({ customer: "all", owner: "all" })} className="h-9 px-2.5 rounded-sm text-[13px] text-ink-2 hover:text-danger flex items-center gap-1 transition-colors"><X className="h-3.5 w-3.5" /> Clear</button>
        )}
        <div className="ml-auto text-[13px] text-ink-2 font-mono-data">{filtered.length} shown</div>
      </div>

      <div className="bg-surface border border-line rounded-md overflow-hidden">
        <div className="overflow-x-auto mv-scroll">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line bg-surface-alt">
                {["Job #", "Customer", "Status", "Last Update", "Next Due", "Owner", ""].map((h) => (
                  <th key={h} className="op-label px-4 py-2.5 whitespace-nowrap font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {filtered.map((j) => {
                const lu = lastUpdate(j);
                const overdue = isOverdue(j.customer_update_due_date);
                return (
                  <tr key={j.id} data-testid={`cu-row-${j.job_number}`} tabIndex={0} onClick={() => navigate(`/app/shipments/${j.id}?focus=customer-updates`)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/app/shipments/${j.id}?focus=customer-updates`); } }} className="hover:bg-surface-alt transition-colors cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-steel/40">
                    <td className="px-4 py-3 font-mono-data text-[12.5px] text-ink font-medium whitespace-nowrap">{j.job_number}</td>
                    <td className="px-4 py-3 text-[13.5px] text-ink whitespace-nowrap">{j.customer_name}</td>
                    <td className="px-4 py-3 text-[13px] text-ink-2 whitespace-nowrap">{j.current_stage}</td>
                    <td className="px-4 py-3 text-[13px] whitespace-nowrap">
                      {lu ? <span className="text-ink-2">{timeAgo(lu.sent_at)}</span> : <span className="text-[11px] px-1.5 py-0.5 rounded-sm bg-[#ECEFF2] text-ink-3 font-medium">Not sent</span>}
                    </td>
                    <td className="px-4 py-3 text-[13px] whitespace-nowrap">
                      <span className={overdue ? "text-warn font-medium" : "text-ink-2"}>{dueLabel(j.customer_update_due_date)}</span>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-ink-2 whitespace-nowrap">{j.assigned_owner}</td>
                    <td className="px-4 py-3"><ArrowRight className="h-4 w-4 text-ink-3 opacity-0 group-hover:opacity-100 transition-opacity" /></td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-ink-3">No updates match this view.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
